import { Controller } from "@hotwired/stimulus"

// The leaderboard's mode/category nav scrolls away with the page like any
// normal nav. Once the hero <h1> scrolls up under the top, a condensed sticky
// bar — the "dock": title + mode selector + category — slides in already in its
// final form and stays put. Scroll back up and it slides away.
//
// The dock is a fixed overlay (out of flow), so showing/hiding it shifts no
// layout: no content jump, and no scroll-anchoring feedback to flicker against.
// That lets the trigger be a single IntersectionObserver with a constant inset
// (the dock's own height) — no re-measuring, no hysteresis.
//
// Two small JS jobs remain: align the dock's content to the page's column (a
// fixed element can't inherit the column's width), and place the category
// popover under its trigger (the Popover API renders it in the top layer).
export default class extends Controller<HTMLElement> {
  static targets = ["dock", "dockInner", "sentinel", "catSummary", "popover"]

  declare readonly dockTarget: HTMLElement
  declare readonly hasDockTarget: boolean
  declare readonly dockInnerTarget: HTMLElement
  declare readonly hasDockInnerTarget: boolean
  declare readonly sentinelTarget: HTMLElement
  declare readonly hasSentinelTarget: boolean
  declare readonly catSummaryTarget: HTMLElement
  declare readonly hasCatSummaryTarget: boolean
  declare readonly popoverTarget: HTMLElement
  declare readonly hasPopoverTarget: boolean

  private observer?: IntersectionObserver
  private docked = false

  connect(): void {
    if (!this.hasDockTarget || !this.hasSentinelTarget) return

    // Keyboard/SR users navigate via the in-flow nav (canonical); the dock is a
    // pointer convenience, so keep its duplicated controls out of the tab order.
    this.dockTarget.setAttribute("aria-hidden", "true")
    this.dockTarget.querySelectorAll<HTMLElement>("a, button").forEach((el) => {
      el.tabIndex = -1
    })

    this.alignToColumn()
    this.observe()

    window.addEventListener("resize", this.onResize, { passive: true })
    if (this.hasPopoverTarget && this.hasCatSummaryTarget) {
      this.popoverTarget.addEventListener("toggle", this.onPopoverToggle as EventListener)
    }
  }

  disconnect(): void {
    this.observer?.disconnect()
    this.observer = undefined
    window.removeEventListener("resize", this.onResize)
    if (this.hasPopoverTarget) {
      this.popoverTarget.removeEventListener("toggle", this.onPopoverToggle as EventListener)
    }
    window.removeEventListener("resize", this.reposition)
    window.removeEventListener("scroll", this.reposition)
  }

  // Reveal the dock once the hero title has scrolled up under where the dock
  // sits. Shrinking the observer root from the top by the dock's height makes
  // "not intersecting" mean "the title is fully behind the dock", so the dock
  // covers the title's last sliver as it slides in — a clean hand-off, no
  // duplicate title. The dock's height is fixed, so the inset is a constant.
  private observe(): void {
    const inset = Math.round(this.dockTarget.offsetHeight) || 56
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.setDocked(!entry.isIntersecting)
      },
      { rootMargin: `-${inset}px 0px 0px 0px`, threshold: 0 }
    )
    this.observer.observe(this.sentinelTarget)
  }

  private setDocked(value: boolean): void {
    if (value === this.docked) return
    this.docked = value
    this.dockTarget.dataset.docked = String(value)
    if (!value && this.hasPopoverTarget) {
      ;(this.popoverTarget as HTMLElement & { hidePopover?: () => void }).hidePopover?.()
    }
  }

  private onResize = (): void => {
    this.alignToColumn()
  }

  // A fixed full-width bar can't inherit the page column's width, so mirror the
  // column's gutters onto the dock's inner padding (the controller element IS
  // the column). The frosted background still spans the full viewport.
  private alignToColumn(): void {
    if (!this.hasDockInnerTarget) return
    const rect = this.element.getBoundingClientRect()
    const vw = document.documentElement.clientWidth
    this.dockInnerTarget.style.paddingLeft = `${Math.round(rect.left)}px`
    this.dockInnerTarget.style.paddingRight = `${Math.round(vw - rect.right)}px`
  }

  private onPopoverToggle = (event: Event): void => {
    const open = (event as Event & { newState?: string }).newState === "open"
    this.catSummaryTarget.dataset.open = String(open)
    if (open) {
      this.reposition()
      window.addEventListener("resize", this.reposition, { passive: true })
      window.addEventListener("scroll", this.reposition, { passive: true })
    } else {
      window.removeEventListener("resize", this.reposition)
      window.removeEventListener("scroll", this.reposition)
    }
  }

  // Pin the popover just under its trigger, right-aligned, clamped to the
  // viewport. The Popover API places it in the top layer (viewport-relative).
  private reposition = (): void => {
    if (!this.hasPopoverTarget || !this.hasCatSummaryTarget) return
    const pop = this.popoverTarget
    const rect = this.catSummaryTarget.getBoundingClientRect()
    const gutter = 8
    const width = pop.offsetWidth
    const left = Math.max(gutter, Math.min(rect.right - width, window.innerWidth - width - gutter))
    pop.style.left = `${Math.round(left)}px`
    pop.style.top = `${Math.round(rect.bottom + 8)}px`
  }
}
